import React, {useRef} from 'react';

import {IMASK_DEFINITIONS, IMASK_PATTERNS} from '../../constants/imaskPatterns';
import {IMaskInputWrapper} from '../../shared/components/IMaskInputWrapper';
import {usePsrCodeInput} from '../../shared/hooks/usePsrCodeInput';
import type {PsrItem} from '../../shared/hooks/usePsrCodeInput';
import './InputCodePsr.style.scss';

/**
 * Props for InputCodePsr component
 */
export interface InputCodePsrProps {
  /** PSR item configuration */
  psrItem: PsrItem;
  /** Whether the PSR code is a CNPJ */
  psrCodeIsCnpj?: boolean;
  /** Pre-filled PSR code */
  psrCode?: string;
  /** List of errors for PSR inputs */
  haveErrorIntoListPsr: Array<{id: string | number; error: boolean}>;
  /** Function to update error list */
  setErrorIntoListPsr: (
    errors: Array<{id: string | number; error: boolean}>
  ) => void;
}

/**
 * Input component for PSR (Payment Service Provider) codes
 *
 * Features:
 * - Supports both regular codes and CNPJ format
 * - Validates minimum length requirements
 * - Handles prefix formatting automatically
 * - Integrates with chat store for state management
 * - Real-time validation with visual feedback
 *
 * @example
 * ```tsx
 * <InputCodePsr
 *   psrItem={{
 *     id: 'ifood',
 *     name: 'iFood',
 *     idMultivanSize: 10,
 *     idMultivanSizeMin: 8
 *   }}
 *   haveErrorIntoListPsr={[]}
 *   setErrorIntoListPsr={setErrors}
 * />
 * ```
 */
export const InputCodePsr: React.FC<InputCodePsrProps> = (props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const {inputCodePsr, styleEmpty, formattedPsrCode, handleChange, handleBlur} =
    usePsrCodeInput({
      psrItem: props.psrItem,
      psrCode: props.psrCode,
      psrCodeIsCnpj: props.psrCodeIsCnpj,
      haveErrorIntoListPsr: props.haveErrorIntoListPsr,
      setErrorIntoListPsr: props.setErrorIntoListPsr
    });

  return (
    <div className="content-code" data-testid="input-content">
      {!props.psrCodeIsCnpj && (
        <input
          ref={inputRef}
          type="text"
          id={`psr-code-${props.psrItem.id}`}
          data-aut-test={`psr-code-${props.psrItem.id}`}
          data-testid="input-code-psr"
          data-code-psr="data-code-psr"
          data-name-psr={props.psrItem.name}
          value={inputCodePsr}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          className={`content-code__psr ${styleEmpty}`}
          placeholder="Digite o código de afiliação"
        />
      )}
      {props.psrCodeIsCnpj && (
        <IMaskInputWrapper
          type="text"
          id={`psr-code-${props.psrItem.id}`}
          data-aut-test={`psr-code-${props.psrItem.id}`}
          data-testid="input-code-psr"
          data-code-psr="data-code-psr"
          data-name-psr={props.psrItem.name}
          value={formattedPsrCode}
          onChange={() => {}}
          mask={IMASK_PATTERNS.CNPJ}
          definitions={IMASK_DEFINITIONS.CNPJ}
          placeholder="Digite o CNPJ"
          className="content-code__psr"
          disabled={true}
        />
      )}
    </div>
  );
};
